/**
 * MOSA-408. What the review screen shows for a probed sheet, derived here so
 * the rules are testable without rendering the wizard.
 *
 * The screen answers three questions before any datasource exists: which tabs
 * become panels, which columns the panel cannot write, and whether this Google
 * account may write at all.
 */

import type { SheetsWizard, SheetsWizardColumn, SheetsWizardTab } from "./sheets-wizard-api";

/**
 * Mirrors `MAX_PRESELECTED_TABS` in
 * `onlydash-api/src/database/connectors/google-sheets.connector.ts`. The app
 * only reports the limit; the probe applies it.
 */
export const MAX_PRESELECTED_TABS = 20;

/** The rows the header-row chooser offers. A banner is never 10 rows tall. */
export const HEADER_ROW_CHOICES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

/**
 * A sheet this close to the cell limit is read in pages rather than in one
 * call, so the review screen says so before the panel opens.
 */
const PAGED_SHARE = 0.5;

/** Why a column is read-only. Both can apply; the order is stable. */
export function columnBadges(column: SheetsWizardColumn): string[] {
  const badges: string[] = [];
  if (column.formula) {
    badges.push("read-only (formula)");
  }
  if (column.protected) {
    badges.push("protected");
  }
  return badges;
}

/** True when any column of the tab cannot be written. */
export const hasReadOnlyColumns = (tab: SheetsWizardTab): boolean =>
  tab.columns.some((column) => column.formula || column.protected);

/**
 * What to fix on this tab, in the order the user should read it. Empty when
 * the tab is ready. Each line names the tab's own problem, so one bad tab does
 * not stop the others.
 */
export function tabProblems(tab: SheetsWizardTab): string[] {
  const problems: string[] = [];
  if (!tab.hasHeader) {
    problems.push(
      tab.headerRow === 1
        ? "Row 1 is blank, so this tab has no column names. Choose the row the names are on."
        : `Row ${tab.headerRow} is blank, so this tab has no column names. Choose another row.`,
    );
  }
  if (tab.mergedHeader) {
    problems.push(
      "A merged cell covers the header row, so the names are unreliable. Unmerge that row in Sheets, or choose a row below it.",
    );
  }
  if (tab.protectedTab) {
    problems.push(
      "This whole tab is protected, so the panel can read it but not change it.",
    );
  }
  return problems;
}

/** The tabs that become panels, and the ones the user left out. */
export const selectedTabs = (probe: SheetsWizard): SheetsWizardTab[] =>
  probe.tabs.filter((tab) => tab.selected);

export const skippedTabs = (probe: SheetsWizard): SheetsWizardTab[] =>
  probe.tabs.filter((tab) => !tab.selected);

/**
 * A tab with no header row can never become a panel: there are no column names
 * to build a table from. The review screen says so per tab, so the user fixes
 * the header instead of waiting for a panel that cannot arrive.
 */
export const canSelectTab = (tab: SheetsWizardTab): boolean => tab.hasHeader;

/**
 * MOSA-668. Tick or untick one tab. A tab with no column names cannot be
 * ticked at all, so the screen cannot ask for a panel the API would refuse.
 */
export function toggleTab(
  probe: SheetsWizard,
  gid: number,
  next: boolean,
): SheetsWizard {
  return {
    ...probe,
    tabs: probe.tabs.map((tab) =>
      tab.gid === gid
        ? { ...tab, selected: next && canSelectTab(tab) }
        : tab,
    ),
  };
}

/** The titles the create call sends, in the order the screen lists them. */
export const selectedTabTitles = (probe: SheetsWizard): string[] =>
  selectedTabs(probe).map((tab) => tab.title);

/**
 * MOSA-668. True when the user has a tab selection to make, so the screen
 * waits for Connect instead of building on its own. One usable tab is not a
 * choice: unticking it would leave nothing to build.
 */
export const hasTabChoice = (probe: SheetsWizard): boolean =>
  probe.tabs.filter(canSelectTab).length > 1;

/** Connect is available once at least one tab is ticked. */
export const canConnect = (probe: SheetsWizard): boolean =>
  selectedTabs(probe).length > 0;

/**
 * True when no tab has column names. Then the sheet is either empty or the
 * names are not where we looked, and the review screen offers both fixes
 * instead of generating an empty panel.
 */
export const needsHeaderRow = (probe: SheetsWizard): boolean =>
  probe.tabs.length > 0 && probe.tabs.every((tab) => !tab.hasHeader);

/** True when the file has no tabs at all: a brand new, untouched spreadsheet. */
export const isEmptySheet = (probe: SheetsWizard): boolean =>
  probe.tabs.length === 0 ||
  probe.tabs.every((tab) => !tab.hasHeader && tab.columns.length === 0);

/**
 * This Google account can open the file but not change it. The panel is still
 * worth having, so the screen offers a read-only connect instead of a dead end.
 */
export const isViewerOnly = (probe: SheetsWizard): boolean => !probe.canEdit;

/**
 * MOSA-408. Re-read one tab from a lower header row. The probe applies
 * `headerRow` to the whole file, so the screen keeps the fresh copy of the one
 * tab the user changed and leaves the others exactly as they were, including
 * which ones are ticked.
 */
export function mergeTabFromProbe(
  current: SheetsWizard,
  fresh: SheetsWizard,
  gid: number,
): SheetsWizard {
  const replacement = fresh.tabs.find((tab) => tab.gid === gid);
  if (!replacement) {
    return current;
  }
  return {
    ...current,
    tabs: current.tabs.map((tab) =>
      tab.gid === gid
        ? {
            ...replacement,
            // A tab that was ticked stays ticked, now that it has names; one
            // that still has no names cannot be ticked at all.
            selected: canSelectTab(replacement) && tab.selected,
          }
        : tab,
    ),
  };
}

/**
 * Whether the wizard may start generating on its own (MOSA-371: no button).
 *
 * It waits for the user whenever the review screen holds a decision: a
 * viewer-only file, where read-only is a choice to make; a sheet with no
 * column names, where there is nothing to build yet; and, since MOSA-668, a
 * file with more than one usable tab, where the user picks which ones become
 * panels. `connectPressed` is that last answer.
 */
export function canAutoGenerate(
  probe: SheetsWizard,
  options: { readOnlyAccepted?: boolean; connectPressed?: boolean } = {},
): boolean {
  if (selectedTabs(probe).length === 0) {
    return false;
  }
  if (isViewerOnly(probe) && !options.readOnlyAccepted) {
    return false;
  }
  if (hasTabChoice(probe) && !options.connectPressed) {
    return false;
  }
  return true;
}

/** The "large: loads in pages" note, or null when the sheet is ordinary. */
export function pagedNote(probe: SheetsWizard): string | null {
  const large =
    probe.maxCells > 0 && probe.totalCells > probe.maxCells * PAGED_SHARE;
  if (!large) {
    return null;
  }
  return "This sheet is large, so the panel loads it in pages instead of all at once.";
}

/** The note for a file with more tabs than the probe preselects. */
export function truncatedTabsNote(probe: SheetsWizard): string | null {
  if (!probe.tabsTruncated) {
    return null;
  }
  const extra = Math.max(0, probe.tabCount - MAX_PRESELECTED_TABS);
  return `This file has ${probe.tabCount} tabs. OnlyDash ticked the first ${MAX_PRESELECTED_TABS}; the other ${extra} are off. Tick any you need.`;
}

/** One line for the header: how much of the sheet becomes a panel. */
export function reviewSummary(probe: SheetsWizard): string {
  const selected = selectedTabs(probe).length;
  const cells = probe.totalCells
    ? ` ${probe.totalCells.toLocaleString()} cells.`
    : "";
  if (selected === 0) {
    return `No tab is ready yet.${cells}`;
  }
  return selected === 1
    ? `1 tab becomes a panel.${cells}`
    : `${selected} tabs become panels.${cells}`;
}
